import { useCallback, useState } from 'react';
import {
  loadBdViewSettings,
  saveBdViewSettings,
  type BdViewSettings,
} from '../lib/bd-view-settings.js';
import { useAuth } from './useAuth.js';

/** 見え方の読みと書き換え。 @graph-connects none */
export interface BdViewSettingsState {
  settings: BdViewSettings;
  /** 今の見え方から次の見え方を作って書く */
  update: (change: (current: BdViewSettings) => BdViewSettings) => void;
}

/**
 * 見ている人の基本設計の見え方を読み、変えたらすぐブラウザに書く。
 *
 * ログインの情報が後から届くと、見ている人（キー）が変わる。そのときは新しい人の見え方を
 * 読み直す（前の人の見え方を新しい人のキーに書かない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の画面の個人の見え方を、ログイン中の人のメールごとにブラウザから読み書きする
 * @graph-connects useAuth [calls] 見ている人のメール
 * @graph-connects loadBdViewSettings [calls] 見え方の読み込み
 * @graph-connects saveBdViewSettings [calls] 見え方の保存
 */
export function useBdViewSettings(): BdViewSettingsState {
  const { user } = useAuth();
  const email = user?.email ?? null;
  const [state, setState] = useState(() => ({ email, settings: loadBdViewSettings(email) }));
  const current = state.email === email ? state : { email, settings: loadBdViewSettings(email) };
  if (current !== state) setState(current);

  const update = useCallback(
    (change: (settings: BdViewSettings) => BdViewSettings) => {
      setState((prev) => {
        const base = prev.email === email ? prev.settings : loadBdViewSettings(email);
        const next = change(base);
        saveBdViewSettings(email, next);
        return { email, settings: next };
      });
    },
    [email],
  );

  return { settings: current.settings, update };
}
